package pl.decks.personal.components.widgets.dnahelix

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.varabyte.kobweb.compose.dom.GenericTag
import com.varabyte.kobweb.compose.dom.svg.Line
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Rect
import com.varabyte.kobweb.compose.dom.svg.SVGPathAttrsScope
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.w3c.css.masking.SVGMaskElement
import org.w3c.dom.svg.SVGElement
import pl.decks.personal.components.widgets.rememberElementSize
import kotlin.math.*
import kotlin.random.Random



private data class HelixGeometry(val x: Double, val top: Double, val bottom: Double)
private data class HelixRung(val x: Double, val start: Double, val end: Double)

private enum class HelixStrand { TOP, BOTTOM; }

private data class HelixSegment(
    val topStrandColor: Color,
    val bottomStrandColor: Color,
    val strandWidth: Double,
    val yCenter: Double,
    val amplitude: Double,
    val xStart: Double,
    val xEnd: Double,
    val drawRungsFrom: HelixStrand,
    val numberRungs: Int,
    val rungsGap: Double,
) {
    fun topY(t: Double) = yCenter + amplitude * sin(0.5 * PI + t * PI)
    fun bottomY(t: Double) = yCenter + amplitude * sin(1.5 * PI + t * PI)
}

@Composable
fun DnaHelix(
    segments: Int = 6,
    pointsPerSegment: Int = 4,
    rungGap: Double = 25.0,
    strandWidth: Double = 6.0,
    mainStrainColor: Color? = null,
    secondaryStrainColor: Color? = null,
    meshId: String? = null,
    modifier: Modifier = Modifier,
    renderPoint: @Composable (Int) -> Unit,
) {
    val mId = remember { meshId ?: "dna-helix-${Random.nextInt(0, Int.MAX_VALUE)}" }
    val (boxWidth, boxHeight) = rememberElementSize(mId).value
    val defaultColor = ColorMode.current.toPalette().color

    Box(Modifier.id(mId).then(modifier)) {
        if (boxWidth <= 0.0 || boxHeight <= 0) return@Box

        val helixSegments = (0 until segments).mapIndexed { index, seg ->
            val isEven = index % 2 == 1
            HelixSegment(
                topStrandColor = (if (isEven) mainStrainColor else secondaryStrainColor) ?: defaultColor,
                bottomStrandColor = (if (isEven) secondaryStrainColor else mainStrainColor) ?: defaultColor,
                strandWidth = strandWidth,
                yCenter = boxHeight / 2.0,
                amplitude = 0.95 * boxHeight / 2.0,
                xStart = seg * boxWidth / segments,
                xEnd = (seg + 1) * boxWidth / segments,
                drawRungsFrom = if (isEven) HelixStrand.TOP else HelixStrand.BOTTOM,
                numberRungs = pointsPerSegment,
                rungsGap = rungGap,
            )
        }
        var slotIndex = 0
        helixSegments.forEachIndexed { ind, seg ->
            HelixSegmentDrawing(seg, numberSamples = 50, maskId = "$mId-mask-$ind")
            seg.rungs().forEach { rung ->
                Box(
                    modifier = Modifier
                        .position(Position.Relative)
                        .left(rung.x.px)
                        .top(rung.end.px)
                        .translate((-50).percent, (-50).percent),
                    contentAlignment = Alignment.Center,
                ) {
                    renderPoint(slotIndex++)
                }
            }
        }
    }
}


private fun HelixSegment.strands(numberSamples: Int): List<HelixGeometry> = (0 until numberSamples).map { step ->
    val t = step.toDouble() / (numberSamples - 1)
    HelixGeometry(
        x = xStart + t * (xEnd - xStart),
        top = topY(t),
        bottom = bottomY(t),
    )
}


private fun HelixSegment.rungs(): List<HelixRung> {
    fun rungAt(t: Double): HelixRung {
        val top = topY(t)
        val bottom = bottomY(t)
        val (start, otherStrand) = if (this.drawRungsFrom == HelixStrand.TOP) top to bottom else bottom to top
        return HelixRung(
            x = xStart + t * (xEnd - xStart),
            start = start,
            end = if (otherStrand > start) otherStrand - rungsGap else otherStrand + rungsGap,
        )
    }
    return if (numberRungs % 2 == 0) {
        val positions = (1..numberRungs + 1).map { rung ->
            rungAt(t = (rung.toDouble() - 0.5) / (numberRungs + 1))
        }
        positions.filterIndexed { index, _ -> index != positions.size / 2 }
    } else {
        val positions = (0..numberRungs).map { rung ->
            rungAt(t = (rung.toDouble() + 0.1) / numberRungs)
        }
        positions.dropLast(1)
    }
}

@Composable
private fun HelixSegmentDrawing(
    segment: HelixSegment,
    numberSamples: Int,
    maskId: String,
) = Svg(
    attrs = Modifier
        .fillMaxWidth()
        .fillMaxHeight()
        .toAttrs {
            attr("width", "100%")
            attr("height", "100%")
            attr("xmlns", "http://www.w3.org/2000/svg")
        }
) {
    val strands = segment.strands(numberSamples)

    GenericTag<SVGMaskElement>("mask", "http://www.w3.org/2000/svg", attrs = {
        attr("id", maskId)
        attr("maskUnits", "userSpaceOnUse")
    }
    ) {
        Rect {
            attr("x", "0")
            attr("y", "0")
            attr("width", "100%")
            attr("height", "100%")
            attr("fill", "white")
        }
        HelixSegmentStrand(
            points = strands.map { it.x to it.top },
            width = segment.strandWidth * 2.5,
            color = Colors.Black,
        )
    }

    segment.rungs().forEach { rung ->
        HelixSegmentRung(
            x = rung.x,
            yStart = rung.start,
            yEnd = rung.end,
            width = segment.strandWidth * 0.5,
            color = if (segment.drawRungsFrom == HelixStrand.TOP) segment.topStrandColor
            else segment.bottomStrandColor,
        )
    }

    HelixSegmentStrand(
        points = strands.map { it.x to it.bottom },
        width = segment.strandWidth,
        color = segment.bottomStrandColor,
    ) {
        attr("mask", "url(#$maskId")
    }
    HelixSegmentStrand(
        points = strands.map { it.x to it.top },
        width = segment.strandWidth,
        color = segment.topStrandColor,
    )
}


@Composable
private fun ElementScope<SVGElement>.HelixSegmentStrand(
    points: List<Pair<Double, Double>>,
    color: Color,
    width: Double,
    addedScope: SVGPathAttrsScope.() -> Unit = {}
) = Path {
    attr("d", pathD(points))
    attr("fill", "none")
    attr("stroke", color.toString())
    attr("stroke-width", width.toString())
    attr("stroke-linecap", "round")
    attr("stroke-linejoin", "round")
    addedScope()
}

@Composable
private fun ElementScope<SVGElement>.HelixSegmentRung(
    x: Double,
    yStart: Double,
    yEnd: Double,
    color: Color,
    width: Double,
) = Line {
    attr("x1", x.toString())
    attr("y1", yStart.toString())
    attr("x2", x.toString())
    attr("y2", yEnd.toString())
    attr("stroke", color.toString())
    attr("stroke-width", width.toString())
    attr("stroke-linecap", "round")
}

private fun pathD(points: List<Pair<Double, Double>>): String = buildString {
    points.forEachIndexed { index, p ->
        if (index == 0) append("M ${p.first} ${p.second}")
        else append("L ${p.first} ${p.second}")
    }
}
